/**
 * Security Utilities for P2P Exchange Application
 * Comprehensive protection against various attack vectors
 */

// Rate limiting storage
const rateLimitStore = new Map<string, { count: number; resetTime: number }>();

// Security configuration
const SECURITY_CONFIG = {
    MAX_REQUESTS_PER_MINUTE: 60,
    MAX_REQUESTS_PER_HOUR: 1000,
    MAX_INPUT_LENGTH: 1000,
    ALLOWED_CURRENCIES: ['USD', 'EUR', 'IRR', 'GBP', 'JPY', 'CAD', 'AUD', 'CHF'],
    ALLOWED_NUMERIC_CHARS: /^[0-9.,]+$/,
    ALLOWED_ALPHANUMERIC: /^[a-zA-Z0-9\s\-_]+$/,
    SANITIZE_HTML: true,
    ENABLE_CSP: true,
    ENABLE_HSTS: true,
    SESSION_TIMEOUT: 30 * 60 * 1000, // 30 minutes
} as const;

/**
 * Input Validation and Sanitization
 */
export class SecurityValidator {
    /**
     * Validate and sanitize currency input
     */
    static validateCurrency(currency: string): { isValid: boolean; sanitized?: string; error?: string } {
        if (!currency || typeof currency !== 'string') {
            return { isValid: false, error: 'Invalid currency format' };
        }

        const sanitized = currency.trim().toUpperCase();

        if (!SECURITY_CONFIG.ALLOWED_CURRENCIES.includes(sanitized as any)) {
            return { isValid: false, error: 'Currency not supported' };
        }

        return { isValid: true, sanitized };
    }

    /**
     * Validate and sanitize numeric input (amounts, rates)
     */
    static validateNumeric(value: string): { isValid: boolean; sanitized?: number; error?: string } {
        if (!value || typeof value !== 'string') {
            return { isValid: false, error: 'Invalid numeric input' };
        }

        // Check length
        if (value.length > SECURITY_CONFIG.MAX_INPUT_LENGTH) {
            return { isValid: false, error: 'Input too long' };
        }

        // Check for allowed characters only
        if (!SECURITY_CONFIG.ALLOWED_NUMERIC_CHARS.test(value)) {
            return { isValid: false, error: 'Invalid characters in numeric input' };
        }

        // Convert to number and validate
        const numeric = parseFloat(value.replace(',', ''));

        if (isNaN(numeric)) {
            return { isValid: false, error: 'Invalid number format' };
        }

        if (numeric < 0) {
            return { isValid: false, error: 'Negative values not allowed' };
        }

        if (numeric > Number.MAX_SAFE_INTEGER) {
            return { isValid: false, error: 'Number too large' };
        }

        return { isValid: true, sanitized: numeric };
    }

    /**
     * Sanitize HTML content to prevent XSS
     */
    static sanitizeHTML(html: string): string {
        if (!SECURITY_CONFIG.SANITIZE_HTML) return html;

        return html
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#x27;')
            .replace(/\//g, '&#x2F;');
    }

    /**
     * Validate and sanitize general text input
     */
    static validateText(text: string, maxLength: number = 100): { isValid: boolean; sanitized?: string; error?: string } {
        if (!text || typeof text !== 'string') {
            return { isValid: false, error: 'Invalid text input' };
        }

        if (text.length > maxLength) {
            return { isValid: false, error: `Text too long (max ${maxLength} characters)` };
        }

        // Remove potentially dangerous characters
        const sanitized = text
            .trim()
            .replace(/[<>]/g, '')
            .replace(/javascript:/gi, '')
            .replace(/on\w+=/gi, '');

        return { isValid: true, sanitized };
    }

    /**
     * Validate API endpoint parameters
     */
    static validateAPIParams(params: Record<string, any>): { isValid: boolean; sanitized?: Record<string, any>; error?: string } {
        const sanitized: Record<string, any> = {};

        for (const [key, value] of Object.entries(params)) {
            if (typeof value === 'string') {
                const validation = this.validateText(value, 200);
                if (!validation.isValid) {
                    return { isValid: false, error: `Invalid parameter: ${key}` };
                }
                sanitized[key] = validation.sanitized;
            } else if (typeof value === 'number') {
                if (isNaN(value) || value < 0 || value > Number.MAX_SAFE_INTEGER) {
                    return { isValid: false, error: `Invalid numeric parameter: ${key}` };
                }
                sanitized[key] = value;
            } else {
                return { isValid: false, error: `Unsupported parameter type: ${key}` };
            }
        }

        return { isValid: true, sanitized };
    }
}

/**
 * Rate Limiting Implementation
 */
export class RateLimiter {
    /**
     * Check if request is within rate limits
     */
    static checkRateLimit(identifier: string, window: 'minute' | 'hour' = 'minute'): { allowed: boolean; remaining: number; resetTime: number } {
        const now = Date.now();
        const key = `${identifier}:${window}`;
        const limit = window === 'minute' ? SECURITY_CONFIG.MAX_REQUESTS_PER_MINUTE : SECURITY_CONFIG.MAX_REQUESTS_PER_HOUR;
        const windowMs = window === 'minute' ? 60 * 1000 : 60 * 60 * 1000;

        const current = rateLimitStore.get(key);

        if (!current || now > current.resetTime) {
            // Reset or create new entry
            rateLimitStore.set(key, {
                count: 1,
                resetTime: now + windowMs
            });
            return { allowed: true, remaining: limit - 1, resetTime: now + windowMs };
        }

        if (current.count >= limit) {
            return { allowed: false, remaining: 0, resetTime: current.resetTime };
        }

        // Increment counter
        current.count++;
        rateLimitStore.set(key, current);

        return { allowed: true, remaining: limit - current.count, resetTime: current.resetTime };
    }

    /**
     * Get rate limit info for client
     */
    static getRateLimitInfo(identifier: string): { minute: { remaining: number; resetTime: number }; hour: { remaining: number; resetTime: number } } {
        const minute = this.checkRateLimit(identifier, 'minute');
        const hour = this.checkRateLimit(identifier, 'hour');

        return {
            minute: { remaining: minute.remaining, resetTime: minute.resetTime },
            hour: { remaining: hour.remaining, resetTime: hour.resetTime }
        };
    }
}

/**
 * Security Headers Configuration
 */
export class SecurityHeaders {
    /**
     * Get CSP (Content Security Policy) header
     */
    static getCSPHeader(): string {
        return [
            "default-src 'self'",
            "script-src 'self' 'unsafe-inline' 'unsafe-eval'",
            "style-src 'self' 'unsafe-inline' https://fonts.googleapis.com",
            "font-src 'self' https://fonts.gstatic.com",
            "img-src 'self' data: https:",
            "connect-src 'self' http://localhost:3001",
            "frame-ancestors 'none'",
            "base-uri 'self'",
            "form-action 'self'"
        ].join('; ');
    }

    /**
     * Get security headers object
     */
    static getSecurityHeaders(): Record<string, string> {
        return {
            'X-Content-Type-Options': 'nosniff',
            'X-Frame-Options': 'DENY',
            'X-XSS-Protection': '1; mode=block',
            'Referrer-Policy': 'strict-origin-when-cross-origin',
            'Permissions-Policy': 'geolocation=(), microphone=(), camera=()',
            'Content-Security-Policy': this.getCSPHeader(),
            'Strict-Transport-Security': 'max-age=31536000; includeSubDomains',
            'Cache-Control': 'no-store, no-cache, must-revalidate, proxy-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
        };
    }
}

/**
 * Session Security Management
 */
export class SessionSecurity {
    private static sessions = new Map<string, { userId: string; lastActivity: number; data: any }>();

    /**
     * Create secure session
     */
    static createSession(userId: string, data: any = {}): string {
        const sessionId = this.generateSecureId();
        const now = Date.now();

        this.sessions.set(sessionId, {
            userId,
            lastActivity: now,
            data
        });

        return sessionId;
    }

    /**
     * Validate session
     */
    static validateSession(sessionId: string): { valid: boolean; userId?: string; data?: any } {
        const session = this.sessions.get(sessionId);

        if (!session) {
            return { valid: false };
        }

        const now = Date.now();

        // Check session timeout
        if (now - session.lastActivity > SECURITY_CONFIG.SESSION_TIMEOUT) {
            this.sessions.delete(sessionId);
            return { valid: false };
        }

        // Update last activity
        session.lastActivity = now;
        this.sessions.set(sessionId, session);

        return { valid: true, userId: session.userId, data: session.data };
    }

    /**
     * Generate secure random ID
     */
    private static generateSecureId(): string {
        const array = new Uint8Array(32);
        crypto.getRandomValues(array);
        return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
    }

    /**
     * Clean expired sessions
     */
    static cleanupSessions(): void {
        const now = Date.now();
        for (const [sessionId, session] of this.sessions.entries()) {
            if (now - session.lastActivity > SECURITY_CONFIG.SESSION_TIMEOUT) {
                this.sessions.delete(sessionId);
            }
        }
    }
}

/**
 * Encryption Utilities
 */
export class Encryption {
    /**
     * Hash sensitive data (one-way)
     */
    static async hashData(data: string): Promise<string> {
        const encoder = new TextEncoder();
        const dataBuffer = encoder.encode(data);
        const hashBuffer = await crypto.subtle.digest('SHA-256', dataBuffer);
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    }

    /**
     * Generate secure random token
     */
    static generateSecureToken(): string {
        const array = new Uint8Array(16);
        crypto.getRandomValues(array);
        return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
    }
}

/**
 * Audit Logging
 */
export class SecurityAudit {
    private static auditLog: Array<{
        timestamp: number;
        action: string;
        userId?: string;
        ip?: string;
        details: any;
        severity: 'low' | 'medium' | 'high' | 'critical';
    }> = [];

    private static apiBaseUrl = 'http://localhost:3002/api/security';
    private static wsUrl = 'ws://localhost:3002';

    /**
     * Initialize WebSocket connection for real-time updates
     */
    private static initializeWebSocket(): WebSocket | null {
        try {
            const ws = new WebSocket(this.wsUrl);

            ws.onopen = () => {
                console.log('🔌 WebSocket connected to security API');
            };

            ws.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data);
                    if (data.type === 'SECURITY_EVENT') {
                        // Add new event to local cache
                        this.auditLog.unshift(data.data);

                        // Keep only last 1000 events in memory
                        if (this.auditLog.length > 1000) {
                            this.auditLog = this.auditLog.slice(0, 1000);
                        }
                    }
                } catch (error) {
                    console.error('Error parsing WebSocket message:', error);
                }
            };

            ws.onerror = (error) => {
                console.error('WebSocket error:', error);
            };

            ws.onclose = () => {
                console.log('WebSocket disconnected, attempting to reconnect...');
                setTimeout(() => this.initializeWebSocket(), 5000);
            };

            return ws;
        } catch (error) {
            console.error('Failed to initialize WebSocket:', error);
            return null;
        }
    }

    /**
     * Initialize audit log from API
     */
    private static async initializeAuditLog(): Promise<void> {
        try {
            const response = await fetch(`${this.apiBaseUrl}/events?limit=100`);
            if (response.ok) {
                const data = await response.json();
                this.auditLog = data.events || [];
            } else {
                console.error('Failed to load events from API, using fallback');
                this.addSampleEvents();
            }
        } catch (error) {
            console.error('Failed to load security events from API:', error);
            this.addSampleEvents();
        }
    }

    /**
     * Log security event to backend API
     */
    static async logEvent(action: string, details: any, severity: 'low' | 'medium' | 'high' | 'critical' = 'low', userId?: string, ip?: string): Promise<void> {
        const event = {
            timestamp: Date.now(),
            action,
            userId,
            ip,
            details,
            severity
        };

        // Add to local cache immediately
        this.auditLog.unshift(event);

        // Keep only last 1000 events
        if (this.auditLog.length > 1000) {
            this.auditLog = this.auditLog.slice(0, 1000);
        }

        // Send to backend API
        try {
            const response = await fetch(`${this.apiBaseUrl}/events`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    action,
                    details,
                    severity,
                    userId,
                    ip
                })
            });

            if (!response.ok) {
                console.error('Failed to send event to API:', response.statusText);
            }
        } catch (error) {
            console.error('Error sending event to API:', error);
        }

        // Log critical events to console
        if (severity === 'critical') {
            console.error('🚨 CRITICAL SECURITY EVENT:', event);
        }
    }

    /**
     * Get audit log from API with caching
     */
    static async getAuditLog(): Promise<typeof SecurityAudit.auditLog> {
        // Initialize if not already done
        if (this.auditLog.length === 0) {
            await this.initializeAuditLog();
        }
        return [...this.auditLog];
    }

    /**
     * Get events by severity from API
     */
    static async getEventsBySeverity(severity: 'low' | 'medium' | 'high' | 'critical'): Promise<typeof SecurityAudit.auditLog> {
        try {
            const response = await fetch(`${this.apiBaseUrl}/events?severity=${severity}&limit=100`);
            if (response.ok) {
                const data = await response.json();
                return data.events || [];
            }
        } catch (error) {
            console.error('Error fetching events by severity:', error);
        }

        // Fallback to local cache
        if (this.auditLog.length === 0) {
            await this.initializeAuditLog();
        }
        return this.auditLog.filter(event => event.severity === severity);
    }

    /**
     * Get security statistics from API
     */
    static async getSecurityStats(days: number = 7): Promise<any> {
        try {
            const response = await fetch(`${this.apiBaseUrl}/stats?days=${days}`);
            if (response.ok) {
                return await response.json();
            }
        } catch (error) {
            console.error('Error fetching security stats:', error);
        }

        return {
            stats: [],
            recent: [],
            topIPs: [],
            totalEvents: 0
        };
    }

    /**
     * Get security status from API
     */
    static async getSecurityStatus(): Promise<any> {
        try {
            const response = await fetch(`${this.apiBaseUrl}/status`);
            if (response.ok) {
                return await response.json();
            }
        } catch (error) {
            console.error('Error fetching security status:', error);
        }

        return {
            blockedIPs: [],
            suspiciousPatterns: 0,
            allowedOrigins: [],
            rateLimitStore: [],
            lastUpdated: new Date().toISOString()
        };
    }

    /**
     * Clear all security events from API
     */
    static async clearAuditLog(days?: number): Promise<{ success: boolean; message: string }> {
        try {
            const url = days ? `${this.apiBaseUrl}/events?days=${days}` : `${this.apiBaseUrl}/events`;
            const response = await fetch(url, {
                method: 'DELETE'
            });

            if (response.ok) {
                const result = await response.json();
                this.auditLog = []; // Clear local cache
                return result;
            }
        } catch (error) {
            console.error('Error clearing audit log:', error);
        }

        return { success: false, message: 'Failed to clear events' };
    }

    /**
     * Get events count by severity
     */
    static async getEventsCount(): Promise<{ low: number; medium: number; high: number; critical: number; total: number }> {
        try {
            const stats = await this.getSecurityStats(1); // Last 24 hours
            const recent = stats.recent || [];

            const counts = {
                low: 0,
                medium: 0,
                high: 0,
                critical: 0,
                total: 0
            };

            recent.forEach((item: any) => {
                counts[item.severity as keyof typeof counts] = parseInt(item.count);
                counts.total += parseInt(item.count);
            });

            return counts;
        } catch (error) {
            console.error('Error getting events count:', error);
        }

        // Fallback to local cache
        if (this.auditLog.length === 0) {
            await this.initializeAuditLog();
        }

        return {
            low: this.auditLog.filter(e => e.severity === 'low').length,
            medium: this.auditLog.filter(e => e.severity === 'medium').length,
            high: this.auditLog.filter(e => e.severity === 'high').length,
            critical: this.auditLog.filter(e => e.severity === 'critical').length,
            total: this.auditLog.length
        };
    }

    /**
     * Add sample security events for demonstration
     */
    private static addSampleEvents(): void {
        const sampleEvents = [
            {
                timestamp: Date.now() - 3600000, // 1 hour ago
                action: 'ADMIN_LOGIN',
                userId: 'HesyMesy',
                ip: '192.168.1.100',
                details: { success: true, method: 'password' },
                severity: 'medium' as const
            },
            {
                timestamp: Date.now() - 1800000, // 30 minutes ago
                action: 'RATE_LIMIT_EXCEEDED',
                userId: 'unknown',
                ip: '203.0.113.45',
                details: { requests: 65, limit: 60, window: 'minute' },
                severity: 'high' as const
            },
            {
                timestamp: Date.now() - 900000, // 15 minutes ago
                action: 'SUSPICIOUS_ACTIVITY',
                userId: 'unknown',
                ip: '198.51.100.23',
                details: { pattern: 'SQL injection attempt', payload: 'SELECT * FROM users' },
                severity: 'critical' as const
            },
            {
                timestamp: Date.now() - 300000, // 5 minutes ago
                action: 'INVALID_INPUT',
                userId: 'guest',
                ip: '192.168.1.101',
                details: { field: 'currency', value: '<script>alert("xss")</script>' },
                severity: 'medium' as const
            },
            {
                timestamp: Date.now() - 60000, // 1 minute ago
                action: 'SESSION_EXPIRED',
                userId: 'HesyMesy',
                ip: '192.168.1.100',
                details: { sessionId: 'abc123', duration: '35 minutes' },
                severity: 'low' as const
            }
        ];

        this.auditLog = sampleEvents;
    }

    /**
     * Initialize WebSocket connection
     */
    static initialize(): void {
        this.initializeWebSocket();
    }
}

/**
 * Main Security Manager
 */
export class SecurityManager {
    /**
     * Validate and sanitize all inputs
     */
    static validateInputs(inputs: Record<string, any>): { isValid: boolean; sanitized?: Record<string, any>; errors?: string[] } {
        const errors: string[] = [];
        const sanitized: Record<string, any> = {};

        for (const [key, value] of Object.entries(inputs)) {
            if (typeof value === 'string') {
                if (key.includes('currency')) {
                    const validation = SecurityValidator.validateCurrency(value);
                    if (!validation.isValid) {
                        errors.push(`${key}: ${validation.error}`);
                    } else {
                        sanitized[key] = validation.sanitized;
                    }
                } else if (key.includes('amount') || key.includes('rate')) {
                    const validation = SecurityValidator.validateNumeric(value);
                    if (!validation.isValid) {
                        errors.push(`${key}: ${validation.error}`);
                    } else {
                        sanitized[key] = validation.sanitized;
                    }
                } else {
                    const validation = SecurityValidator.validateText(value);
                    if (!validation.isValid) {
                        errors.push(`${key}: ${validation.error}`);
                    } else {
                        sanitized[key] = validation.sanitized;
                    }
                }
            } else {
                sanitized[key] = value;
            }
        }

        return {
            isValid: errors.length === 0,
            sanitized: errors.length === 0 ? sanitized : undefined,
            errors: errors.length > 0 ? errors : undefined
        };
    }

    /**
     * Check rate limits for user/IP
     */
    static checkRateLimits(identifier: string): { allowed: boolean; info: any } {
        const minuteCheck = RateLimiter.checkRateLimit(identifier, 'minute');
        const hourCheck = RateLimiter.checkRateLimit(identifier, 'hour');

        const allowed = minuteCheck.allowed && hourCheck.allowed;

        if (!allowed) {
            SecurityAudit.logEvent('RATE_LIMIT_EXCEEDED', { identifier, minuteCheck, hourCheck }, 'high');
        }

        return {
            allowed,
            info: {
                minute: minuteCheck,
                hour: hourCheck
            }
        };
    }

    /**
     * Log security event
     */
    static logSecurityEvent(action: string, details: any, severity: 'low' | 'medium' | 'high' | 'critical' = 'low', userId?: string): void {
        SecurityAudit.logEvent(action, details, severity, userId);
    }

    /**
     * Get security headers
     */
    static getSecurityHeaders(): Record<string, string> {
        return SecurityHeaders.getSecurityHeaders();
    }

    /**
     * Check if IP is blocked and handle suspicious activity
     */
    static async checkIPBlocking(ip: string, requestData?: any): Promise<{ blocked: boolean; reason?: string }> {
        // Import IPBlockingService dynamically to avoid circular dependencies
        const { IPBlockingOptimizedService } = await import('../services/ipBlockingOptimizedService');

        // Check if IP is blocked
        const blockStatus = await IPBlockingOptimizedService.isIPBlocked(ip);
        if (blockStatus.blocked) {
            return blockStatus;
        }

        // Detect suspicious activity if request data is provided
        if (requestData) {
            IPBlockingOptimizedService.detectSuspiciousActivity(ip, requestData);
        }

        return { blocked: false };
    }

    /**
     * Block IP address
     */
    static async blockIP(ip: string, reason: string, severity: 'low' | 'medium' | 'high' | 'critical' = 'medium'): Promise<{ success: boolean; message: string }> {
        const { IPBlockingOptimizedService } = await import('../services/ipBlockingOptimizedService');
        return await IPBlockingOptimizedService.blockIP(ip, reason, severity);
    }

    /**
     * Unblock IP address
     */
    static async unblockIP(ip: string): Promise<{ success: boolean; message: string }> {
        const { IPBlockingOptimizedService } = await import('../services/ipBlockingOptimizedService');
        return await IPBlockingOptimizedService.unblockIP(ip);
    }
}

export default SecurityManager; 